import { GlobalStyle } from '@community_dev/pixels'
import { ThemeProvider } from '@community_dev/pixels'
import LIGHT from '@community_dev/pixels/dist/js/themes/presets/light'
import { QueryClientProvider, QueryClient } from '@tanstack/react-query'
import mixpanel from 'mixpanel-browser'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { createGlobalStyle } from 'styled-components'

import { App } from './App'

import { LaunchDarklyProvider } from 'contexts/LaunchDarklyProvider'
import { UserProvider } from 'contexts/UserProvider'
import Sentry from 'integrations/Sentry'

import 'react-app-polyfill/stable'

declare global {
  interface Window {
    Cypress: unknown
  }
}

if (import.meta.env.VITE_MIXPANEL_TOKEN) {
  mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN)
  mixpanel.register({
    App: 'Data Export',
    Platform: 'Web',
  })

  mixpanel.set_config({ debug: import.meta.env.VITE_MIXPANEL_DEBUG === 'true' })
}

if (import.meta.env.DEV && !window.Cypress) {
  import('./mocks/browser')
    .then(({ worker }) => {
      const publicUrl = import.meta.env.VITE_PUBLIC_URL || ''
      worker.start({
        serviceWorker: {
          url: `${publicUrl.replace(/\/$/, '')}/mockServiceWorker.js`,
        },
        onUnhandledRequest: 'bypass',
      })
    })
    .catch(Sentry.captureException)
}

const StyledGlobal = createGlobalStyle`
  * {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    box-sizing: border-box;
  }

  body {
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: -0.096px;
  }

  #root {
    position: fixed;
    height: 100%;
    width: 100%;
  }
`

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
})

const container = document.getElementById('root')
const root = createRoot(container!)

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={LIGHT}>
        <GlobalStyle />
        <StyledGlobal />
        <UserProvider>
          <LaunchDarklyProvider>
            <App />
          </LaunchDarklyProvider>
        </UserProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>,
)
